/**
 * harperUpdate — Harper's Update as a person reads it, and the record of how
 * it was written. Shared by the run's case file and the Line page's preview,
 * which both read the same `HarperUpdate` and `HarperGenerationReport` shapes.
 *
 *   - `HarperUpdateView`: the headline, the lead, the sections, and the also
 *     line, each line with its receipts ("#2167", "+2", "see all").
 *   - `HowItWasWritten`: a collapsed section (the review case's `CaseSection`)
 *     with what Harper read, what the Approach resolved to and from where,
 *     every change's placement and reason (cuts included), the check, and
 *     each step.
 */

import {
  APPROACH_KEYS,
  APPROACH_SETTING_LABEL,
  STYLE_KEYS,
  STYLE_LABEL,
  WRITTEN_AS_COPY,
  WRITTEN_AS_SETTING_LABEL,
  approachValueLabel,
  normalizeWrittenAs,
  type ApproachSource,
  jobFailureCopy,
  type HarperGenerationReport,
  type HarperPlacement,
  type HarperReceipt,
  type HarperReportItem,
  type HarperStageName,
  type HarperUpdate,
} from "@yagni/shared";
import { Chip } from "../ui/index.js";
import { Markdown } from "../components/Markdown.js";
import { CaseSection } from "./review/CaseSection.js";
import "./review/reviewCase.css";

/** A step's name, as the report and the stage list both say it. */
export const HARPER_STEP_LABEL: Record<HarperStageName, string> = {
  gather: "Collect",
  dossiers: "Read",
  editor: "Edit",
  writer: "Write",
  critic: "Check",
  deliver: "Post",
};

const PLACEMENT_WORD: Record<HarperPlacement, string> = {
  lead: "lead",
  notable: "notable",
  also: "also",
  cut: "cut",
};

const PLACEMENT_TONE: Record<HarperPlacement, "success" | "neutral" | "dashed"> = {
  lead: "success",
  notable: "success",
  also: "neutral",
  cut: "dashed",
};

function Receipts({ receipts }: { receipts: readonly HarperReceipt[] }) {
  if (receipts.length === 0) return null;
  return (
    <span className="hupd-receipts">
      {receipts.map((receipt) => (
        <a
          key={`${receipt.label}-${receipt.url}`}
          href={receipt.url}
          target="_blank"
          rel="noreferrer"
          data-testid="hupd-receipt"
        >
          <Chip tone="dashed">{receipt.label}</Chip>
        </a>
      ))}
    </span>
  );
}

/** Who made a line, before its sentence (older Updates carry it in their text instead). */
function By({ authors }: { authors?: readonly string[] }) {
  if (!authors || authors.length === 0) return null;
  return (
    <span className="hupd-by" data-testid="hupd-by">
      {authors.join(", ")}
    </span>
  );
}

/** The Update, as the channel carries it. */
export function HarperUpdateView({ update }: { update: HarperUpdate }) {
  return (
    <div className="hupd" data-testid="hupd">
      <p className="hrun-filing-headline" data-testid="hupd-headline">
        {update.headline}
      </p>
      {update.lead && (
        <div className="hupd-lead" data-testid="hupd-lead">
          <By authors={update.lead.authors} />
          <Markdown text={update.lead.text} className="hupd-lead-text" />
          <Receipts receipts={update.lead.receipts} />
        </div>
      )}
      <div className="hrun-filing-body">
        {update.sections.map((section, index) => (
          <div key={`${section.head}-${index}`} className="hrun-filing-section">
            {section.head && (
              <div className="hrun-filing-section-head">
                <Markdown text={section.head} />
              </div>
            )}
            <ul className="hrun-filing-lines">
              {section.lines.map((line, i) => (
                <li key={i} className="hrun-filing-line" data-testid="hupd-line">
                  <span className="hrun-filing-line-body">
                    <By authors={line.authors} />
                    <Markdown text={line.text} className="hrun-filing-line-text" />
                  </span>
                  <Receipts receipts={line.receipts} />
                </li>
              ))}
            </ul>
          </div>
        ))}
        {update.also && (
          <div className="hupd-also" data-testid="hupd-also">
            <Markdown text={update.also.text} className="hrun-filing-line-text" /> <Receipts receipts={update.also.receipts} />
          </div>
        )}
      </div>
    </div>
  );
}

/** The fallback notes a report carries, as sentences (empty when nothing fell back). */
export function degradedSentences(report: HarperGenerationReport | null): string[] {
  if (!report) return [];
  return [...new Set(report.quality.degraded.map((entry) => jobFailureCopy(entry.code).sentence))];
}

/** "58 read · 1 lead · 5 notable · 11 also · 41 cut · checked"; a list template has no lead to count. */
export function reportSummary(report: HarperGenerationReport): string {
  const count = (placement: HarperPlacement) =>
    report.items.filter((item) => item.placement === placement).reduce((n, item) => n + item.members.length, 0);
  const check =
    report.checks.critic === "pass" ? "checked" : report.checks.critic === "revised" ? "revised once" : "check skipped";
  const leads = count("lead");
  return [
    `${report.read.candidates} read`,
    ...(leads > 0 ? [`${leads} lead`] : []),
    `${count("notable")} notable`,
    `${count("also")} also`,
    `${count("cut")} cut`,
    check,
  ].join(" · ");
}

function sourceWords(report: HarperGenerationReport, key: (typeof APPROACH_KEYS)[number]): string {
  return sourceOf(report.approach.sources[key]);
}

function sourceOf(source: ApproachSource | undefined): string {
  if (!source || source.source === "default") return "Harper's default";
  if (source.source === "set") return "set by you";
  return source.quote ? `from your instructions: "${source.quote}"` : "from your instructions";
}

function msWords(ms: number | null): string {
  if (ms === null) return "";
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}

function ItemRow({ item }: { item: HarperReportItem }) {
  return (
    <li className={"hrep-item" + (item.placement === "cut" ? " hrep-item-cut" : "")} data-testid="hrep-item">
      <div className="hrep-item-head">
        <Chip tone={PLACEMENT_TONE[item.placement]}>{PLACEMENT_WORD[item.placement]}</Chip>
        <span className="hrep-item-title">{item.title}</span>
      </div>
      <p className="hrep-item-reason">{item.reason}</p>
      {item.line && <p className="hrep-item-line">Posted as: {item.line}</p>}
      <ul className="hrep-members">
        {item.members.map((member) => (
          <li key={member.findingId} className="hrep-member">
            {member.url ? (
              <a href={member.url} target="_blank" rel="noreferrer">
                {member.label ?? member.title}
              </a>
            ) : (
              <span>{member.label ?? ""}</span>
            )}{" "}
            <span className="hrep-member-title">{member.title}</span>
            {member.dossier && (
              <span className="hrep-member-card">
                {member.dossier.whatChanged}
                {" · "}
                {member.dossier.userFacing ? "users notice" : `${member.dossier.whoNotices} notice`}
                {" · "}
                {member.dossier.fallback ? "read from the title only" : `${member.dossier.confidence} confidence`}
              </span>
            )}
          </li>
        ))}
      </ul>
    </li>
  );
}

/** How the Update was written: collapsed at the bottom of the page. */
export function HowItWasWritten({ report }: { report: HarperGenerationReport }) {
  const order: HarperPlacement[] = ["lead", "notable", "also", "cut"];
  const items = [...report.items].sort((a, b) => order.indexOf(a.placement) - order.indexOf(b.placement));
  const failedChecks = report.checks.codeChecks.filter((check) => !check.passed);
  return (
    <CaseSection title="How this was written" summary={reportSummary(report)} testId="hrep">
      <div className="hrep" data-testid="hrep-body">
        <section className="hrep-block">
          <h4 className="hrep-title">What Harper read</h4>
          <p className="hrep-text">
            {report.read.candidates} changes from{" "}
            {new Date(report.read.windowStart).toLocaleString(undefined, { month: "short", day: "numeric" })} to{" "}
            {new Date(report.read.windowEnd).toLocaleString(undefined, { month: "short", day: "numeric" })}.
            {report.read.alreadyPosted > 0 && ` ${report.read.alreadyPosted} were already in a previous Update.`}
            {report.read.capped > 0 && ` ${report.read.capped} past the reading cap were judged from their titles.`}
          </p>
          {report.read.unreadSources.length > 0 && (
            <p className="hrep-text">Not read this time: {report.read.unreadSources.join(", ")}.</p>
          )}
        </section>

        <section className="hrep-block">
          <h4 className="hrep-title">Approach</h4>
          <ul className="hrep-list" data-testid="hrep-approach">
            {report.approach.writtenAs && (
              <li data-testid="hrep-written-as">
                {WRITTEN_AS_SETTING_LABEL}: <strong>{WRITTEN_AS_COPY[normalizeWrittenAs(report.approach.writtenAs)].label}</strong>
              </li>
            )}
            {APPROACH_KEYS.map((key) => (
              <li key={key}>
                {APPROACH_SETTING_LABEL[key]}: <strong>{approachValueLabel(key, report.approach.resolved[key])}</strong>{" "}
                <span className="hrep-muted">({sourceWords(report, key)})</span>
              </li>
            ))}
            {report.approach.style &&
              STYLE_KEYS.map((key) => (
                <li key={key} data-testid={`hrep-style-${key}`}>
                  {STYLE_LABEL[key]}: <strong>{report.approach.style!.resolved[key]}</strong>{" "}
                  <span className="hrep-muted">({sourceOf(report.approach.style!.sources[key])})</span>
                </li>
              ))}
          </ul>
          {report.approach.conflicts.map((conflict) => (
            <p key={conflict} className="hrep-conflict" data-testid="hrep-conflict">
              {conflict}
            </p>
          ))}
        </section>

        <section className="hrep-block">
          <h4 className="hrep-title">Every change</h4>
          <ul className="hrep-items">
            {items.map((item) => (
              <ItemRow key={item.storyId} item={item} />
            ))}
          </ul>
        </section>

        <section className="hrep-block">
          <h4 className="hrep-title">The check</h4>
          <p className="hrep-text" data-testid="hrep-critic">
            {report.checks.critic === "pass"
              ? "A second, stronger model read the Update against its records and found nothing wrong."
              : report.checks.critic === "revised"
                ? "The check found issues; the Update was revised once."
                : "The second model could not check this Update; the built-in checks alone reviewed it."}
          </p>
          {report.checks.issues.length > 0 && (
            <ul className="hrep-list">
              {report.checks.issues.map((issue, i) => (
                <li key={i}>
                  {issue.kind.replaceAll("_", " ")}: {issue.detail}
                </li>
              ))}
            </ul>
          )}
          {report.checks.revision.map((pair, i) => (
            <div key={i} className="hrep-revision" data-testid="hrep-revision">
              <p className="hrep-muted">Before: {pair.before}</p>
              <p>After: {pair.after}</p>
            </div>
          ))}
          {failedChecks.length > 0 && (
            <ul className="hrep-list">
              {failedChecks.map((check) => (
                <li key={check.check}>
                  {check.check}: {check.detail}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="hrep-block">
          <h4 className="hrep-title">Each step</h4>
          <ul className="hrep-list">
            {report.stages.map((stage) => (
              <li key={stage.stage}>
                {HARPER_STEP_LABEL[stage.stage]}:{" "}
                {stage.status === "degraded"
                  ? `fell back (${jobFailureCopy(stage.code).phrase})`
                  : stage.status === "skipped"
                    ? "did not run"
                    : "done"}
                {stage.ms !== null && <span className="hrep-muted"> · {msWords(stage.ms)}</span>}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </CaseSection>
  );
}
